/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React from "react";

import { connect } from "react-redux";
import { Dispatch } from "redux";

import { ErrorMessage, FastField, Form, Formik } from "formik";
import * as Yup from "yup";

import { IDropdownOption, PrimaryButton, Spinner, SpinnerSize, Stack, Text } from "@fluentui/react";

import * as EventActions from "../../Dataflows/Event/EventAction";

import { withAuthProvider } from "Authentication/AuthProvider";
import { RedText } from "Components/Common/FormikErrorMessage/FormikErrorMessage.styles";
import { RedTextDiv } from "Components/ReactHookForm/HookErrorMessage/HookErrorMessage.styles";

import { approvalTypes } from "../../Constants/EventConstants";
import {
  getSelectedEventId,
  getUpdateEventStatusError,
  isUpdatingStatus,
} from "../../Dataflows/Event/EventSelector";
import { AppState } from "../../Dataflows/Reducers";
import { IEventStatus } from "../../Interfaces/Event/IEventStatus";
import FormikDropdown from "../Common/Dropdown/FormikDropdown";
import {
  approvalFromStackTokens,
  commentStyles,
  dropdownStyles,
  labelTextStyles,
} from "./EventApprovalForm.styles";
import { IEventApprovalFormProps } from "./IEventApprovalFormProps";

const approvalOptions: IDropdownOption[] = approvalTypes.map((approvalType) => ({
  key: approvalType.key,
  text: approvalType.text,
}));

class EventApprovalForm extends React.Component<IEventApprovalFormProps> {
  render(): React.ReactElement<IEventApprovalFormProps> {
    const {
      isUpdatingStatus,
      selectedEventId,
      updateEventStatusError,
      authProvider,
      eventStatusName,
      eventFormatName,
      inPersonAttendees,
      onDemandViews,
      liveOnlineViews,
      actions: { updateEventStatus },
    } = this.props;

    const { account } = authProvider;

    const EventApprovalSchema = Yup.object().shape({
      approvalType: Yup.string().required("Status is required."),
      inPersonAttendees: Yup.number().test({
        name: "inPersonAttendees-validation",
        message: "In Person Attendees cannot be negative.",
        test: (value) =>
          eventStatusName === "Submitted" &&
          (eventFormatName === "Hybrid" || eventFormatName === "In-Person")
            ? value >= 0
            : true,
      }),
      onDemandViews: Yup.number().test({
        name: "onDemandViews-validation",
        message: "On Demand Views cannot be negative.",
        test: (value) =>
          eventStatusName === "Submitted" &&
          (eventFormatName === "Hybrid" || eventFormatName === "Online")
            ? value >= 0
            : true,
      }),
      liveOnlineViews: Yup.number().test({
        name: "liveOnlineViews-validation",
        message: "Live Online Views cannot be negative.",
        test: (value) =>
          eventStatusName === "Submitted" &&
          (eventFormatName === "Hybrid" || eventFormatName === "Online")
            ? value >= 0
            : true,
      }),
      comment: Yup.string().when("approvalType", {
        is: (value) => value === "Returned" || value === "Denied",
        then: Yup.string().required("Feedback is required"),
        otherwise: Yup.string(),
      }),
    });

    if (isUpdatingStatus) {
      return <Spinner size={SpinnerSize.large} />;
    } else if (updateEventStatusError !== "") {
      return (
        <div>
          <Text>{updateEventStatusError.toString()}</Text>
        </div>
      );
    }

    const renderField = (name: string, label: string): React.ReactElement => (
      <Stack horizontal key={`${name}Stack`}>
        <Text styles={labelTextStyles}>{label}:</Text>
        <FastField
          name={name}
          as="input"
          type="number"
          className="form-input"
          placeholder={label}
          style={commentStyles}
        />
        <ErrorMessage
          className="error"
          name={name}
          component="div"
          render={(message) => (
            <RedTextDiv role="alert" aria-relevant="all">
              <RedText className="error">{message}</RedText>
            </RedTextDiv>
          )}
        />
      </Stack>
    );

    const renderReachFields = (): React.ReactElement | null => {
      if (eventStatusName === "Submitted") {
        switch (eventFormatName) {
          case "Hybrid":
            return (
              <>
                {renderField("liveOnlineViews", "Live Online Views")}
                {renderField("onDemandViews", "On-Demand Views")}
                {renderField("inPersonAttendees", "In-Person Attendees")}
              </>
            );
          case "In-Person":
            return renderField("inPersonAttendees", "In-Person Attendees");
          case "Online":
            return (
              <>
                {renderField("liveOnlineViews", "Live Online Views")}
                {renderField("onDemandViews", "On-Demand Views")}
              </>
            );
          default:
            return null;
        }
      }
      return null;
    };

    return (
      <Formik
        initialValues={{
          eventIdentifier: selectedEventId,
          approvalType: "",
          comment: "",
          inPersonAttendees: eventStatusName === "Submitted" ? 0 : inPersonAttendees,
          onDemandViews: eventStatusName === "Submitted" ? 0 : onDemandViews,
          liveOnlineViews: eventStatusName === "Submitted" ? 0 : liveOnlineViews,
          createdBy: account.userName,
        }}
        validationSchema={EventApprovalSchema}
        onSubmit={(values, { setSubmitting, resetForm }): void => {
          setSubmitting(false);
          const eventStatus: IEventStatus = {
            eventIdentifier: values.eventIdentifier,
            approvalType: values.approvalType,
            comment: values.comment,
            inPersonAttendees: values.inPersonAttendees,
            onDemandViews: values.onDemandViews,
            liveOnlineViews: values.liveOnlineViews,
            createdBy: values.createdBy,
          };
          updateEventStatus(eventStatus);
          resetForm();
        }}
      >
        {({ isSubmitting, setFieldValue, values }): React.ReactNode => (
          <Form>
            <Stack tokens={approvalFromStackTokens}>
              <Stack horizontal key="approvalTypeStack">
                <Text styles={labelTextStyles}>Approval type:</Text>
                <FastField
                  name="approvalType"
                  value={values.approvalType}
                  placeholder="Select approval type"
                  onChange={(_: React.MouseEvent<HTMLElement>, value: any): void => {
                    setFieldValue("approvalType", value.key);
                  }}
                  options={approvalOptions}
                  component={FormikDropdown}
                  styles={dropdownStyles}
                />
              </Stack>
              <>{renderReachFields()}</>
              <Stack horizontal key="approvalFeedbackStack">
                <Text styles={labelTextStyles}>Feedback:</Text>
                <FastField
                  name="comment"
                  as="textarea"
                  rows={4}
                  className="form-input"
                  placeholder="Feedback"
                  style={commentStyles}
                />
                <ErrorMessage name="comment" component="div" />
              </Stack>
              <Stack horizontal key="approvalButtonStack">
                <Stack.Item>
                  <Text />
                </Stack.Item>
                <PrimaryButton text="Assign Status" disabled={isSubmitting} type="submit" />
              </Stack>
            </Stack>
          </Form>
        )}
      </Formik>
    );
  }
}

/**
 * Maps StudentsContainer to redux state
 * @param {AppState} state The App state.
 */
const mapStateToProps = (state: AppState, ownProps: IEventApprovalFormProps & any) => ({
  isUpdatingStatus: isUpdatingStatus(state.eventState),
  selectedEventId: getSelectedEventId(state.eventState),
  updateEventStatusError: getUpdateEventStatusError(state.eventState),
});

/**
 * Maps Component's dispatch/handler props to redux state
 * @param {Dispatch} dispatch The dispatch.
 */
const mapDispatchToProps = (dispatch: Dispatch<EventActions.EventActionTypes>) => ({
  actions: {
    updateEventStatus: (eventStatus: IEventStatus): EventActions.EventActionTypes =>
      dispatch(EventActions.updateEventStatus(eventStatus)),
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(withAuthProvider(EventApprovalForm));
